import type { FieldExplainerContent } from './types';

export type EmergencyFundExplainerContext = {
  input: {
    housing: number;
    utilities: number;
    groceries: number;
    transportation: number;
    insurance: number;
    debtPayments: number;
    other: number;
    currentSavings: number;
    monthlyContribution: number;
    employment: 'stable' | 'contract' | 'self' | 'variable';
    incomeSources: 'single' | 'dual' | 'multiple';
    dependents: 'none' | 'some' | 'many';
    jobMarket: 'good' | 'hot' | 'tough';
  };
  result: {
    totalMonthlyExpenses: number;
    recommendedMonths: number;
    targetEmergencyFund: number;
    currentMonthsCovered: number;
    progressPercent: number;
    timeToGoal: string;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const employmentAdjustment: Record<string, number> = { stable: 0, contract: 1, self: 2, variable: 2 };
const incomeSourcesAdjustment: Record<string, number> = { single: 0, dual: -1, multiple: -1 };
const dependentsAdjustment: Record<string, number> = { none: 0, some: 1, many: 2 };
const jobMarketAdjustment: Record<string, number> = { good: 0, hot: -1, tough: 2 };

const employmentLabel: Record<string, string> = { stable: 'stable salaried', contract: 'contract', self: 'self-employed', variable: 'variable income' };
const incomeSourcesLabel: Record<string, string> = { single: 'single income source', dual: 'dual income', multiple: 'multiple income sources' };
const dependentsLabel: Record<string, string> = { none: 'no dependents', some: 'some dependents', many: 'many dependents' };
const jobMarketLabel: Record<string, string> = { good: 'a good job market', hot: 'a hot job market', tough: 'a tough job market' };

export function getEmergencyFundFieldExplainers(ctx: EmergencyFundExplainerContext): FieldExplainerContent[] {
  const {
    housing,
    utilities,
    groceries,
    transportation,
    insurance,
    debtPayments,
    other,
    currentSavings,
    monthlyContribution,
    employment,
    incomeSources,
    dependents,
    jobMarket
  } = ctx.input;
  const { totalMonthlyExpenses, recommendedMonths, targetEmergencyFund, currentMonthsCovered, progressPercent, timeToGoal } = ctx.result;

  const expenseList = `${usd.format(housing)} + ${usd.format(utilities)} + ${usd.format(groceries)} + ${usd.format(transportation)} + ${usd.format(insurance)} + ${usd.format(debtPayments)} + ${usd.format(other)}\n  = ${usd.format(totalMonthlyExpenses)}`;

  const baseMonths = 3;
  const rawMonths =
    baseMonths +
    employmentAdjustment[employment] +
    incomeSourcesAdjustment[incomeSources] +
    dependentsAdjustment[dependents] +
    jobMarketAdjustment[jobMarket];
  const monthsFormula = `3 base\n${employmentAdjustment[employment] >= 0 ? '+' : ''}${employmentAdjustment[employment]} (${employmentLabel[employment]})\n${incomeSourcesAdjustment[incomeSources] >= 0 ? '+' : ''}${incomeSourcesAdjustment[incomeSources]} (${incomeSourcesLabel[incomeSources]})\n${dependentsAdjustment[dependents] >= 0 ? '+' : ''}${dependentsAdjustment[dependents]} (${dependentsLabel[dependents]})\n${jobMarketAdjustment[jobMarket] >= 0 ? '+' : ''}${jobMarketAdjustment[jobMarket]} (${jobMarketLabel[jobMarket]})\n= ${rawMonths}, clamped to [3, 9]\n= ${recommendedMonths} months`;

  return [
    {
      id: 'housing',
      kind: 'input',
      label: 'Housing',
      plain:
        'Rent or mortgage — the expense that would keep coming due even if income stopped tomorrow. Unlike the Budget Planner, this calculator does not sort expenses into needs/wants/savings; every category here is treated as an essential the fund must cover.',
      formula: `housing = ${usd.format(housing)}\ntotalMonthlyExpenses = ${expenseList}`,
      feeds: 'Total monthly expenses, which is multiplied by the recommended months to set the fund target.'
    },
    {
      id: 'utilities',
      kind: 'input',
      label: 'Utilities',
      plain: 'Electricity, gas, water, and similar bills that continue whether or not you have a paycheck.',
      formula: `utilities = ${usd.format(utilities)}\ntotalMonthlyExpenses = ${expenseList}`,
      feeds: 'Total monthly expenses, which is multiplied by the recommended months to set the fund target.'
    },
    {
      id: 'groceries',
      kind: 'input',
      label: 'Groceries',
      plain: 'Food to keep the household running during the gap this fund is meant to cover.',
      formula: `groceries = ${usd.format(groceries)}\ntotalMonthlyExpenses = ${expenseList}`,
      feeds: 'Total monthly expenses, which is multiplied by the recommended months to set the fund target.'
    },
    {
      id: 'transportation',
      kind: 'input',
      label: 'Transportation',
      plain: 'Car payment, insurance, gas, or transit costs needed to keep getting around, job or no job.',
      formula: `transportation = ${usd.format(transportation)}\ntotalMonthlyExpenses = ${expenseList}`,
      feeds: 'Total monthly expenses, which is multiplied by the recommended months to set the fund target.'
    },
    {
      id: 'insurance',
      kind: 'input',
      label: 'Insurance',
      plain: 'Health, life, or property premiums that stay due on their own schedule regardless of income.',
      formula: `insurance = ${usd.format(insurance)}\ntotalMonthlyExpenses = ${expenseList}`,
      feeds: 'Total monthly expenses, which is multiplied by the recommended months to set the fund target.'
    },
    {
      id: 'debtPayments',
      kind: 'input',
      label: 'Debt payments',
      plain: 'Minimum payments on loans and credit cards. Missing these has consequences, so the calculator counts them as essential spending, not discretionary.',
      formula: `debtPayments = ${usd.format(debtPayments)}\ntotalMonthlyExpenses = ${expenseList}`,
      feeds: 'Total monthly expenses, which is multiplied by the recommended months to set the fund target.'
    },
    {
      id: 'other',
      kind: 'input',
      label: 'Other essentials',
      plain: 'A catch-all for any other must-pay monthly cost that does not fit the categories above.',
      formula: `other = ${usd.format(other)}\ntotalMonthlyExpenses = ${expenseList}`,
      feeds: 'Total monthly expenses, which is multiplied by the recommended months to set the fund target.'
    },
    {
      id: 'currentSavings',
      kind: 'input',
      label: 'Current savings',
      plain: 'What you already have set aside specifically for this fund, measured against the target and used as the starting point for the savings trajectory chart.',
      formula:
        totalMonthlyExpenses > 0
          ? `currentSavings = ${usd.format(currentSavings)}\nmonths already covered = ${usd.format(currentSavings)} / ${usd.format(totalMonthlyExpenses)} = ${currentMonthsCovered.toFixed(1)}`
          : `currentSavings = ${usd.format(currentSavings)}\nwith $0 of monthly expenses there is nothing\nto cover, so months covered reads 0`,
      feeds: 'Progress percentage, months already covered, time to goal, and the starting point of the trajectory chart.'
    },
    {
      id: 'monthlyContribution',
      kind: 'input',
      label: 'Monthly contribution',
      plain: 'How much new money you plan to add each month. The trajectory chart assumes this exact amount lands every single month with no gaps.',
      formula:
        targetEmergencyFund - currentSavings <= 0
          ? `${usd.format(targetEmergencyFund)} − ${usd.format(currentSavings)} ≤ 0\nthe target is already reached, so no\nmonths-to-goal calculation is needed`
          : monthlyContribution > 0
            ? `remaining = ${usd.format(targetEmergencyFund)} − ${usd.format(currentSavings)}\nmonths = ceil(remaining / ${usd.format(monthlyContribution)})\n${timeToGoal}`
            : `monthlyContribution = $0\nno new savings are being added, so "${timeToGoal}"`,
      feeds: 'Time to goal and every point on the savings trajectory chart after the starting month.',
      assumption:
        monthlyContribution <= 0
          ? 'With no monthly contribution, the chart still needs to draw something: it shows a flat 24-month line at your current savings level rather than actually reaching the target.'
          : undefined
    },
    {
      id: 'employment',
      kind: 'input',
      label: 'Employment stability',
      plain:
        'How likely your income is to survive a rough patch. Contract, self-employed, and variable-income work add months to the recommendation because there is no employer safety net or guaranteed next paycheck.',
      formula: `employment: ${employmentLabel[employment]}\nadjustment: ${employmentAdjustment[employment] >= 0 ? '+' : ''}${employmentAdjustment[employment]} month${Math.abs(employmentAdjustment[employment]) === 1 ? '' : 's'}\n\n${monthsFormula}`,
      feeds: 'Recommended months, which directly sets the dollar target.'
    },
    {
      id: 'incomeSources',
      kind: 'input',
      label: 'Income sources',
      plain: 'More independent income streams mean one of them failing is less catastrophic, so each additional source trims the recommended cushion.',
      formula: `incomeSources: ${incomeSourcesLabel[incomeSources]}\nadjustment: ${incomeSourcesAdjustment[incomeSources]} month${Math.abs(incomeSourcesAdjustment[incomeSources]) === 1 ? '' : 's'}\n\n${monthsFormula}`,
      feeds: 'Recommended months, which directly sets the dollar target.'
    },
    {
      id: 'dependents',
      kind: 'input',
      label: 'Dependents',
      plain: 'People who rely on your income besides yourself. More dependents means more mouths to feed if income stops, so the recommendation grows.',
      formula: `dependents: ${dependentsLabel[dependents]}\nadjustment: ${dependentsAdjustment[dependents] >= 0 ? '+' : ''}${dependentsAdjustment[dependents]} month${Math.abs(dependentsAdjustment[dependents]) === 1 ? '' : 's'}\n\n${monthsFormula}`,
      feeds: 'Recommended months, which directly sets the dollar target.'
    },
    {
      id: 'jobMarket',
      kind: 'input',
      label: 'Job market',
      plain: 'How quickly you could likely find replacement income today. A hot market trims the cushion slightly; a tough market adds the largest single adjustment on this page.',
      formula: `jobMarket: ${jobMarketLabel[jobMarket]}\nadjustment: ${jobMarketAdjustment[jobMarket] >= 0 ? '+' : ''}${jobMarketAdjustment[jobMarket]} month${Math.abs(jobMarketAdjustment[jobMarket]) === 1 ? '' : 's'}\n\n${monthsFormula}`,
      feeds: 'Recommended months, which directly sets the dollar target.',
      assumption:
        'The four risk factors are added independently and the result stays between 3 and 9 months. The worst combination this model produces (self-employed, single income, many dependents, tough market) is the full 9; households that want a longer runway than that can simply treat the dollar target as a floor.'
    },
    {
      id: 'targetEmergencyFund',
      kind: 'result',
      label: 'Recommended fund',
      plain: 'Your total monthly essential expenses multiplied by the recommended number of months from the risk factors on this page.',
      formula: `${usd.format(totalMonthlyExpenses)} × ${recommendedMonths} months\n  = ${usd.format(targetEmergencyFund)}`,
      feeds: 'Progress percentage, time to goal, the target line on the trajectory chart, and the milestone table.'
    },
    {
      id: 'progressPercent',
      kind: 'result',
      label: 'Progress',
      plain: 'Current savings measured as a share of the recommended fund. It can read above 100% if your savings already exceed the target.',
      formula:
        targetEmergencyFund > 0
          ? `${usd.format(currentSavings)} / ${usd.format(targetEmergencyFund)} × 100\n  = ${progressPercent}%`
          : `the target is $0 (no monthly expenses\nentered), so progress reads 0% by definition`,
      feeds: 'Nothing further downstream; it is the headline coverage figure.'
    },
    {
      id: 'timeToGoal',
      kind: 'result',
      label: 'Time to goal',
      plain: 'How long the current monthly contribution would take to close the remaining gap to the target, rounded up to the next whole month.',
      formula:
        targetEmergencyFund - currentSavings <= 0
          ? `${usd.format(targetEmergencyFund)} − ${usd.format(currentSavings)} ≤ 0\n"${timeToGoal}"`
          : monthlyContribution > 0
            ? `remaining = ${usd.format(targetEmergencyFund)} − ${usd.format(currentSavings)}\nceil(remaining / ${usd.format(monthlyContribution)}) months\n  = "${timeToGoal}"`
            : `remaining > 0 but monthly contribution is $0\n"${timeToGoal}"`,
      feeds: 'Nothing further downstream; it is a standalone read on pace.',
      assumption:
        'The savings trajectory chart always draws at least a 6-month window, even after the goal is already reached, so there is always something to look at instead of a blank chart.'
    },
    {
      id: 'totalMonthlyExpenses',
      kind: 'result',
      label: 'Monthly expenses',
      plain: 'All seven expense categories added together — the number the fund is sized to replace, one month at a time.',
      formula: expenseList,
      feeds: 'The recommended fund target and the "months already covered" figure.'
    }
  ];
}
